import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, LoaderCircle, ShieldCheck } from "lucide-react";
import { formatEther } from "viem";
import { Button } from "@/components/ui/button";
import { useWallet } from "@/context/WalletContext";
import { CONTRACT_ADDRESS, getMintCost, mintAvatar } from "@/lib/contract";
import { personalityJson } from "@/lib/personality";
import type { PersonalityCard, TraitSelection } from "@/types";

export function MintPanel({ traits, personality, onMinted }: { traits: TraitSelection; personality: PersonalityCard | null; onMinted: (tokenId: bigint) => void }) {
  const { address, walletClient, connect } = useWallet();
  const [accepted, setAccepted] = useState(false);
  const [cost, setCost] = useState<bigint | null>(null);
  const [minting, setMinting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMintCost().then(setCost).catch(() => setCost(null));
  }, []);

  const mint = async () => {
    if (!address || !walletClient || !personality || !accepted) return;
    setMinting(true);
    setError(null);
    try {
      const result = await mintAvatar(walletClient, address, traits, personalityJson(personality));
      onMinted(result.tokenId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Minting failed.");
    } finally {
      setMinting(false);
    }
  };

  return (
    <section className="rounded-[24px] border border-white/10 bg-white/[.035] p-4 sm:p-5">
      <div className="flex items-start justify-between gap-4">
        <div><p className="text-[11px] font-bold tracking-[0.2em] text-amber-200/70">ON-CHAIN BIRTH</p><h2 className="mt-1 text-lg font-bold">Mint the AniSoul</h2></div>
        <span className="rounded-full border border-amber-300/15 bg-amber-300/7 px-3 py-1 text-[10px] font-bold tracking-wider text-amber-100">BASE SEPOLIA</span>
      </div>
      <div className="mt-5 space-y-2.5 text-sm">
        {["SVG image generated by the contract", "Visual combination permanently unique", "Personality limited to 512 public bytes", "Pyth Entropy reveal included"].map((item) => (
          <div className="flex items-center gap-2 text-white/60" key={item}><CheckCircle2 className="size-4 text-emerald-300" />{item}</div>
        ))}
      </div>
      <div className="my-5 flex items-end justify-between rounded-2xl border border-white/8 bg-black/20 p-4">
        <div><p className="text-xs text-white/40">Bundled test mint</p><p className="mt-1 text-xl font-black">{cost ? Number(formatEther(cost)).toFixed(6) : "—"} ETH</p></div>
        <span className="text-right text-[10px] leading-4 text-white/35">0.001 mint<br />+ live Entropy fee</span>
      </div>
      <label className="flex cursor-pointer items-start gap-3 text-xs leading-5 text-white/48">
        <input checked={accepted} className="mt-1 accent-cyan-300" onChange={(event) => setAccepted(event.target.checked)} type="checkbox" />
        <span>I am 18+, approve this public personality card, and understand blockchain records cannot be deleted.</span>
      </label>
      {!address ? (
        <Button className="mt-4 w-full bg-cyan-300 text-[#06100f] hover:bg-cyan-200" onClick={() => void connect()}>Connect wallet</Button>
      ) : (
        <Button className="mt-4 w-full bg-cyan-300 text-[#06100f] hover:bg-cyan-200" disabled={!personality || !accepted || minting || !CONTRACT_ADDRESS} onClick={() => void mint()}>
          {minting ? <LoaderCircle className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}{minting ? "Confirming on Base" : personality ? "Mint unique avatar" : "Complete five chat turns"}
        </Button>
      )}
      {!CONTRACT_ADDRESS && <p className="mt-3 text-center text-xs text-amber-200/65">Preview mode: deploy the contract and set VITE_ANISOUL_CONTRACT to enable minting.</p>}
      {error && <p className="mt-3 text-xs text-red-300">{error}</p>}
      <a className="mt-4 flex items-center justify-center gap-1 text-xs text-white/35 hover:text-cyan-200" href="https://sepolia.basescan.org" rel="noreferrer" target="_blank">View Base Sepolia <ExternalLink className="size-3" /></a>
    </section>
  );
}
